import { queryOptions as ts_queryOptions, useQuery } from '@tanstack/react-query'
import { Address } from 'ox'
import { formatUnits } from 'viem'

import type * as WorkerAssets from '../../worker/lib/assets.js'
import type * as TempoApi from '../../worker/lib/tempo-api.js'
import { api } from './api.js'
import { getSupportedChainId } from './chain.js'
import * as Currency from './currency.js'

/** Returns the React Query key for an account valuation. */
export function queryKey(account: string | null | undefined, chainId?: number) {
  return ['valuation', account?.toLowerCase() ?? null, chainId] as const
}

/** Fetches an account valuation through the wallet worker. */
export async function fetch(options: fetch.Options) {
  const { account, chainId } = options
  const res = await api.api.valuation.$get({
    query: { address: account, chainId: String(chainId) },
  })
  if (!res.ok) throw new Error('Failed to load valuation')
  return (await res.json()) as TempoApi.Valuation
}

/** `fetch` types. */
export declare namespace fetch {
  /** Fetch options. */
  type Options = {
    account: Address.Address
    chainId: number
  }
}

/** Returns React Query options for an account valuation. */
export function queryOptions(options: queryOptions.Options) {
  const { account, chainId } = options
  const chainId_supported = getSupportedChainId(chainId)

  return ts_queryOptions({
    queryKey: queryKey(account, chainId_supported),
    queryFn: () => fetch({ account: account!, chainId: chainId_supported }),
    retry: false,
  })
}

/** `queryOptions` types. */
export declare namespace queryOptions {
  /** Query options. */
  type Options = {
    account?: Address.Address | undefined
    chainId?: number | undefined
  }
}

/** Reads the current account valuation. */
export function useValuation(options: useValuation.Options) {
  const { account, chainId, enabled = true } = options
  return useQuery({
    ...queryOptions({ account, chainId }),
    enabled: enabled && Boolean(account && chainId),
  })
}

/** `useValuation` types. */
export declare namespace useValuation {
  /** Hook options. */
  type Options = {
    account?: Address.Address | undefined
    chainId?: number | undefined
    enabled?: boolean | undefined
  }
}

/** Returns the displayed portfolio total, including unlocked USD zone holdings. */
export function total(options: total.Options) {
  const { assets, fallback, valuation } = options
  if (!valuation?.available) return fallback

  const zones = assets.reduce((sum, asset) => {
    if (Currency.inferCurrency(asset.symbol) !== 'USD') return sum
    const balance = (asset.zones ?? []).reduce((sum, zone) => sum + BigInt(zone.balance), 0n)
    return sum + Number(formatUnits(balance, asset.decimals))
  }, 0)

  return Number(valuation.amount) + zones
}

/** `total` types. */
export declare namespace total {
  /** Function options. */
  type Options = {
    assets: readonly WorkerAssets.list.Asset[]
    fallback: number
    valuation?: TempoApi.Valuation | undefined
  }
}
